import React, { createContext, useContext, useState } from "react";
import clsx from "clsx";
import { IconBrowzwear, IconMiro } from "icons";

/** The card's footer element, for a body piece that owns its own button state (Browzwear's connect form) but must show it in the footer. */
const IntegrationCardFooterContext = createContext<HTMLElement | null>(null);

export const useIntegrationCardFooter = () => useContext(IntegrationCardFooterContext);

/** App-icon style tiles: the mark on the brand's own colours, so the tile reads the same in light and dark (it carries its own background). */
const BRANDS = {
  miro: { Mark: IconMiro, tile: "bg-[#FFD02F] text-[#050038]" },
  browzwear: { Mark: IconBrowzwear, tile: "bg-[#0E1729] text-[#FFFFFF]" },
};

interface IntegrationCardProps {
  name: string;
  /** Which brand mark fills the logo tile. Omitted → a monogram of the name's first letter. */
  brand?: keyof typeof BRANDS;
  /** One line: what the integration does. */
  description?: string;
  /** Status chip content (e.g. "Connected as Ada · Acme" / "Not connected"); omitted while the status is loading. */
  status?: React.ReactNode;
  /** `ok` = connected, `warn` = needs attention, `idle` = not connected. */
  statusTone?: "ok" | "warn" | "idle";
  /** The body below the description. */
  children?: React.ReactNode;
  /** Bottom actions (Connect / Disconnect …). The footer is omitted when empty. */
  actions?: React.ReactNode;
  /** Render an (initially empty) footer that a body piece fills through `useIntegrationCardFooter`. */
  footerSlot?: boolean;
  className?: string;
  bodyClassName?: string;
  "data-testid"?: string;
}

const TONE: Record<NonNullable<IntegrationCardProps["statusTone"]>, string> = {
  ok: "bg-purpleGreen/20 text-purpleGreen",
  warn: "bg-orange/20 text-orange",
  idle: "bg-black-300 text-grey",
};

/**
 * The one frame every integration shares on the Integrations hub (REF-6702): header (mark + name + status chip), a one-line
 * description, the body, and the actions at the bottom. Same bar/body/footer look as the Browzwear connect card, so the two read as peers.
 */
const IntegrationCard = ({ name, brand, description, status, statusTone = "idle", children, actions, footerSlot, className, bodyClassName, "data-testid": testId }: IntegrationCardProps) => {
  const [footerEl, setFooterEl] = useState<HTMLElement | null>(null);

  return (
    <IntegrationCardFooterContext.Provider value={footerEl}>
      <div data-testid={testId} className={clsx("flex min-w-0 flex-col rounded-[5px] border border-black-300 bg-black-200", className)}>
        <div className="flex items-center gap-2 rounded-t-[4px] bg-body-100 p-2.5">
          {brand ? (
            <span aria-hidden="true" className={clsx("flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px]", BRANDS[brand].tile)}>
              {React.createElement(BRANDS[brand].Mark, { className: "h-[60%] w-[60%]" })}
            </span>
          ) : (
            <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] bg-purpleGreen/20 text-head7 text-purpleGreen">
              {name.charAt(0)}
            </span>
          )}
          <h6 className="min-w-0 truncate text-h6 text-white">{name}</h6>
          {status && (
            <span title={typeof status === "string" ? status : undefined} className={clsx("ms-auto min-w-0 max-w-[60%] truncate rounded-full px-2 py-0.5 text-bodyXs", TONE[statusTone])}>
              {status}
            </span>
          )}
        </div>

        <div className={clsx("flex flex-1 flex-col gap-3 bg-body p-4 text-bodySm text-white", bodyClassName)}>
          {description && <p className="text-white/70">{description}</p>}
          {children}
        </div>

        {(actions || footerSlot) && (
          <div ref={setFooterEl} className="flex justify-end gap-2 rounded-b-[4px] border-t border-black-300 bg-black-100 p-2.5">
            {actions}
          </div>
        )}
      </div>
    </IntegrationCardFooterContext.Provider>
  );
};

export default IntegrationCard;
